/**
 * Public Invitation Accept page — /invitation/:token
 *
 * Standalone (no AppHeader, no Login required). Resolves the token, shows
 * tenant/role context, lets the invited user fill in their personal data
 * and AGB-Zustimmung, then redirects to /login with a pre-filled phone.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertTriangle, UserPlus, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const ROLE_LABEL = {
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  sales_rep: "Vertrieb",
  telesales_director: "Telesales-Direktor",
  agent: "Telesales-Agent",
  installer: "Installation",
};

export default function InvitationAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", phone: "", first_name: "", last_name: "" });
  const [acceptAgb, setAcceptAgb] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [legalSlugs, setLegalSlugs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/invitations/${token}`)
      .then((r) => {
        if (cancelled) return;
        setInvitation(r.data);
        setForm({
          email: r.data.target_email || "",
          phone: r.data.target_phone || "",
          first_name: r.data.first_name || "",
          last_name: r.data.last_name || "",
        });
      })
      .catch((e) => setError(e.response?.data?.detail || "Einladung konnte nicht geladen werden"))
      .finally(() => setLoading(false));

    // Load required legal docs
    api.get("/legal/required-acceptances").then((r) => setLegalSlugs(r.data?.required || [])).catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const submit = async () => {
    if (!form.email || !form.phone || !form.first_name || !form.last_name) {
      toast.error("Bitte alle Pflichtfelder ausfüllen"); return;
    }
    if (!acceptAgb) { toast.error("Bitte AGB akzeptieren"); return; }
    setSubmitting(true);
    try {
      await api.post(`/invitations/${token}/accept`, {
        ...form,
        legal_acceptance_slugs: legalSlugs,
      });
      setDone(true);
      toast.success("Konto eingerichtet! Sie können sich jetzt einloggen.");
      setTimeout(() => navigate(`/login?phone=${encodeURIComponent(form.phone)}`), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annahme fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  if (loading) return <CenterCard><Loader2 className="animate-spin text-zinc-500" size={32} /></CenterCard>;
  if (error) return <CenterCard><AlertTriangle className="text-amber-400 mb-3" size={32} /><p className="text-zinc-300">{error}</p></CenterCard>;
  if (done) return (
    <CenterCard>
      <CheckCircle2 className="text-[#00E575] mb-3" size={48} />
      <h2 className="font-display text-xl font-bold mb-2">Konto eingerichtet!</h2>
      <p className="text-zinc-300 text-sm font-medium">Sie werden gleich zum Login weitergeleitet …</p>
    </CenterCard>
  );
  if (!invitation) return <CenterCard><Loader2 className="animate-spin text-zinc-500" size={32} /></CenterCard>;

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-3" data-testid="invitation-accept">
      <div className="w-full max-w-md bg-[#18181B] border border-zinc-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-1">
          <UserPlus className="text-[#00E575]" size={20} />
          <div className="text-[12px] uppercase tracking-widest text-[#00E575] font-bold">Einladung</div>
        </div>
        <h1 className="font-display text-2xl font-bold mb-1">Willkommen!</h1>
        <p className="text-zinc-400 text-sm mb-4">
          Sie wurden eingeladen, dem Mandanten <strong className="text-zinc-100">{invitation.tenant_name}</strong> als{" "}
          <strong className="text-zinc-100">{ROLE_LABEL[invitation.role] || invitation.role}</strong> beizutreten.
        </p>

        <div className="space-y-3">
          <Field label="Vorname *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="inv-first" />
          <Field label="Nachname *" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="inv-last" />
          <Field label="E-Mail *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="inv-email" />
          <Field label="Mobil-Telefon * (für Login-OTP)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="inv-phone" placeholder="+49 …" />

          <label className="flex items-start gap-2 text-[12px] text-zinc-300 cursor-pointer p-3 bg-[#09090B] rounded-lg border border-zinc-800 font-medium">
            <input type="checkbox" checked={acceptAgb} onChange={(e) => setAcceptAgb(e.target.checked)} data-testid="inv-agb" className="mt-0.5 accent-[#00E575]" />
            <span>
              Ich akzeptiere die <a href="/legal/agb" className="text-[#00E575] underline">AGB</a> und{" "}
              <a href="/legal/privacy" className="text-[#00E575] underline">Datenschutz-Erklärung</a>.
            </span>
          </label>

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="inv-submit"
            className="w-full h-11 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold inline-flex items-center justify-center gap-2 disabled:bg-zinc-800 disabled:text-zinc-500"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} /> Konto einrichten</>}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <div>
    <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">{label}</label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium"
    />
  </div>
);

const CenterCard = ({ children }) => (
  <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-3">
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center max-w-md w-full">
      {children}
    </div>
  </div>
);
